"use client"

import { useState, useTransition } from "react"
import {
  Card, Text, Button, Stack, Stepper, SimpleGrid,
  SegmentedControl, Group, Badge, Progress, Alert, Center, Loader,
  Popover, ActionIcon, TextInput, Box, Divider,
} from "@forestsync/ui"
import { TbInfoCircle, TbExternalLink, TbCheck } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { getCountryName } from "@forestsync/db"
import Link from "next/link"
import { runMatchFromWizard, createMandate } from "@/actions/procurement"
import { ProcurementBuyModal } from "./ProcurementBuyModal"
import type { MatchResult } from "@/lib/matching"
import type { WizardCriteria } from "./types"

const VOLUME_PRESETS = [
  { label: "1–50 t", value: "1-50" },
  { label: "50–500 t", value: "50-500" },
  { label: "500–5k t", value: "500-5000" },
  { label: "5,000+ t", value: "5000" },
]

const BUDGET_PRESETS = [
  { label: "Under €30", value: "0-30" },
  { label: "€30–75", value: "30-75" },
  { label: "€75–150", value: "75-150" },
  { label: "€150+", value: "150-999" },
]

const BREAKDOWN_KEYS = ["methodology", "geography", "vintage", "price", "volume"] as const
interface LiteWizardProps {
  methodologies?: string[]
  regions?: string[]
}

export function LiteWizard({ methodologies = [], regions = [] }: LiteWizardProps) {
  const t = useTranslations("procurement")
  const [step, setStep] = useState(0)
  const [isPending, startTransition] = useTransition()

  // Wizard selections
  const [selectedMethodologies, setSelectedMethodologies] = useState<string[]>([])
  const [selectedRegions, setSelectedRegions] = useState<string[]>([])
  const [volumePreset, setVolumePreset] = useState<string>("")
  const [budgetPreset, setBudgetPreset] = useState<string>("")

  // Results
  const [showResults, setShowResults] = useState(false)
  const [results, setResults] = useState<MatchResult[]>([])
  const [error, setError] = useState<string | null>(null)

  // Buy modal
  const [buyTarget, setBuyTarget] = useState<MatchResult | null>(null)

  // Save as mandate
  const [mandateName, setMandateName] = useState("")
  const [mandateSaved, setMandateSaved] = useState(false)
  const [mandateError, setMandateError] = useState<string | null>(null)
  const [isSaving, startSaving] = useTransition()

  function toggleItem(arr: string[], item: string, setter: (v: string[]) => void) {
    setter(arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item])
  }

  function buildCriteria(): WizardCriteria {
    const criteria: WizardCriteria = {
      methodology: selectedMethodologies,
      geography: selectedRegions,
    }
    if (volumePreset) {
      if (volumePreset.includes("-")) {
        const [min, max] = volumePreset.split("-").map(Number)
        criteria.volumeMin = min
        criteria.volumeMax = max
      } else {
        criteria.volumeMin = Number(volumePreset)
      }
    }
    if (budgetPreset) {
      const [min, max] = budgetPreset.split("-").map(Number)
      criteria.priceMin = min
      criteria.priceMax = max
    }
    return criteria
  }

  function handleFindMatches() {
    setError(null)
    setMandateSaved(false)
    setMandateError(null)
    startTransition(async () => {
      const criteria = buildCriteria()
      const result = await runMatchFromWizard(criteria)
      if ("error" in result) {
        setError(result.error)
      } else {
        setResults(result.results)
        setShowResults(true)
        setStep(4)
      }
    })
  }

  function handleRefine() {
    setShowResults(false)
    setStep(3)
  }

  function handleSaveMandate() {
    const name = mandateName.trim()
    if (!name) return
    setMandateError(null)
    startSaving(async () => {
      const result = await createMandate({ name, criteria: buildCriteria() as Record<string, unknown> })
      if ("error" in result) {
        setMandateError(result.error)
      } else {
        setMandateSaved(true)
      }
    })
  }

  const anyMethodology = selectedMethodologies.length === 0
  const anyRegion = selectedRegions.length === 0
  return (
    <Stack gap="lg">
      <Stepper active={step} onStepClick={!showResults ? setStep : undefined} size="sm">
        {/* Step 0: Methodology */}
        <Stepper.Step label={t("stepMethodology")}>
          <Stack gap="md" py="md">
            <Text fw={600} size="sm">{t("methodologyTitle")}</Text>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
              <Card
                shadow="xs"
                padding="sm"
                radius="md"
                withBorder
                style={{
                  cursor: "pointer",
                  borderColor: anyMethodology ? "var(--mantine-color-violet-6)" : undefined,
                  backgroundColor: anyMethodology ? "var(--mantine-color-violet-light)" : undefined,
                }}
                onClick={() => setSelectedMethodologies([])}
              >
                <Text ta="center" fw={500} size="sm" c={anyMethodology ? "violet" : "dimmed"}>{t("anyMethodology")}</Text>
              </Card>
              {methodologies.map((m) => (
                <Card
                  key={m}
                  shadow="xs"
                  padding="sm"
                  radius="md"
                  withBorder
                  style={{
                    cursor: "pointer",
                    borderColor: selectedMethodologies.includes(m) ? "var(--mantine-color-green-6)" : undefined,
                    backgroundColor: selectedMethodologies.includes(m) ? "var(--mantine-color-green-light)" : undefined,
                  }}
                  onClick={() => toggleItem(selectedMethodologies, m, setSelectedMethodologies)}
                >
                  <Text ta="center" fw={500} size="sm">{m}</Text>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>
        </Stepper.Step>

        {/* Step 1: Geography */}
        <Stepper.Step label={t("stepGeography")}>
          <Stack gap="md" py="md">
            <Text fw={600} size="sm">{t("geographyTitle")}</Text>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
              <Card
                shadow="xs"
                padding="sm"
                radius="md"
                withBorder
                style={{
                  cursor: "pointer",
                  borderColor: anyRegion ? "var(--mantine-color-violet-6)" : undefined,
                  backgroundColor: anyRegion ? "var(--mantine-color-violet-light)" : undefined,
                }}
                onClick={() => setSelectedRegions([])}
              >
                <Text ta="center" fw={500} size="sm" c={anyRegion ? "violet" : "dimmed"}>{t("anyRegion")}</Text>
              </Card>
              {regions.map((r) => (
                <Card
                  key={r}
                  shadow="xs"
                  padding="sm"
                  radius="md"
                  withBorder
                  style={{
                    cursor: "pointer",
                    borderColor: selectedRegions.includes(r) ? "var(--mantine-color-blue-6)" : undefined,
                    backgroundColor: selectedRegions.includes(r) ? "var(--mantine-color-blue-light)" : undefined,
                  }}
                  onClick={() => toggleItem(selectedRegions, r, setSelectedRegions)}
                >
                  <Text ta="center" fw={500} size="sm">{r}</Text>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>
        </Stepper.Step>

        {/* Step 2: Volume */}
        <Stepper.Step label={t("stepVolume")}>
          <Stack gap="md" py="md">
            <Text fw={600} size="sm">{t("volumeTitle")}</Text>
            <SegmentedControl
              value={volumePreset}
              onChange={setVolumePreset}
              data={VOLUME_PRESETS}
              fullWidth
            />
            <Text size="sm" c="dimmed">{t("tonnes")} (minimum needed)</Text>
          </Stack>
        </Stepper.Step>

        {/* Step 3: Budget */}
        <Stepper.Step label={t("stepBudget")}>
          <Stack gap="md" py="md">
            <Text fw={600} size="sm">{t("budgetTitle")}</Text>
            <SegmentedControl
              value={budgetPreset}
              onChange={setBudgetPreset}
              data={BUDGET_PRESETS}
              fullWidth
            />
            <Text size="sm" c="dimmed">€ / tCO₂e</Text>
          </Stack>
        </Stepper.Step>

        {/* Step 4: Results */}
        <Stepper.Step label={t("stepResults")}>
          {isPending ? (
            <Center py="xl"><Loader /></Center>
          ) : results.length > 0 ? (
            <Stack gap="md" py="md">
              {results[0].overallScore < 40 ? (
                <Alert color="yellow" variant="light" icon={<TbInfoCircle size={16} />}>
                  {t("noExactMatch")}
                </Alert>
              ) : (
                <Text fw={600} size="sm">{t("resultsTitle")}</Text>
              )}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {results.map((result) => (
                  <Card key={`${result.supplyItem.projectId}-${result.supplyItem.vintage}`} shadow="sm" padding="md" radius="md" withBorder>
                    <Stack gap="xs">
                      <Text fw={600} size="sm">{result.supplyItem.name}</Text>
                      <Group gap="xs" wrap="wrap">
                        <Badge size="sm" variant="light">{result.supplyItem.methodology}</Badge>
                        <Badge size="sm" variant="light" color="blue">{getCountryName(result.supplyItem.country)}</Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">€{result.supplyItem.price.toFixed(2)}/tCO₂e</Text>
                        <Text size="sm" c="dimmed">{result.supplyItem.availableVolume.toFixed(0)}t avail.</Text>
                      </Group>

                      {/* Fit Score with breakdown popover */}
                      <Group gap="xs" align="center">
                        <Text size="xs" c="dimmed">{t("fitScore")}</Text>
                        <Badge
                          color={result.overallScore >= 70 ? "green" : result.overallScore >= 40 ? "yellow" : "red"}
                          size="sm"
                        >
                          {result.overallScore.toFixed(0)}%
                        </Badge>
                        <Popover withArrow shadow="md" withinPortal transitionProps={{ duration: 0 }}>
                          <Popover.Target>
                            <ActionIcon size="xs" variant="subtle" color="gray" title={t("scoreBreakdown")}>
                              <TbInfoCircle size={14} />
                            </ActionIcon>
                          </Popover.Target>
                          <Popover.Dropdown miw={200}>
                            <Stack gap={4} p={4}>
                              <Text size="xs" fw={600} mb={4}>{t("scoreBreakdown")}</Text>
                              {BREAKDOWN_KEYS.map((key) => {
                                const score = result.breakdown[key]
                                return (
                                  <Group key={key} justify="space-between" gap="md">
                                    <Text size="xs" c="dimmed" w={80}>{t(key as "methodology")}</Text>
                                    {score === -1 ? (
                                      <Text size="xs" c="dimmed">{t("notFiltered")}</Text>
                                    ) : (
                                      <Group gap="xs">
                                        <Progress value={Math.round(score)} size="xs" w={60} color={score >= 70 ? "green" : score >= 40 ? "yellow" : "red"} />
                                        <Text size="xs" fw={500}>{Math.round(score)}%</Text>
                                      </Group>
                                    )}
                                  </Group>
                                )
                              })}
                            </Stack>
                          </Popover.Dropdown>
                        </Popover>
                      </Group>
                      <Progress
                        value={result.overallScore}
                        color={result.overallScore >= 70 ? "green" : result.overallScore >= 40 ? "yellow" : "red"}
                        size="sm"
                      />
                      <Button
                        size="sm"
                        color="green"
                        autoContrast
                        fullWidth
                        onClick={() => setBuyTarget(result)}
                      >
                        {t("buyCredits")}
                      </Button>
                      <Button
                        component={Link}
                        href={`/market/${result.supplyItem.projectId}/${result.supplyItem.vintage}`}
                        variant="subtle"
                        size="sm"
                        fullWidth
                        rightSection={<TbExternalLink size={13} />}
                      >
                        {t("viewProject")}
                      </Button>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            </Stack>
          ) : (
            <Stack gap="md" py="xl" align="center">
              <Text fw={500}>{t("noResults")}</Text>
              <Text size="sm" c="dimmed" ta="center" maw={400}>{t("noResultsSuggestion")}</Text>
              <Button variant="light" onClick={() => { setShowResults(false); setStep(0) }}>
                {t("refine")}
              </Button>
            </Stack>
          )}
        </Stepper.Step>
      </Stepper>

      {error && (
        <Alert color="red" onClose={() => setError(null)} withCloseButton>
          {error}
        </Alert>
      )}

      {/* Navigation */}
      {!showResults && (
        <Group justify="space-between">
          <Button variant="default" disabled={step === 0} onClick={() => setStep(step - 1)}>
            {t("back")}
          </Button>
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)}>{t("next")}</Button>
          ) : (
            <Button color="green" autoContrast loading={isPending} onClick={handleFindMatches}>
              {t("findSupply")}
            </Button>
          )}
        </Group>
      )}

      {showResults && results.length > 0 && (
        <Stack gap="sm">
          <Group justify="center">
            <Button variant="light" onClick={handleRefine}>
              {t("refine")}
            </Button>
          </Group>

          <Divider label={t("saveAsMandate")} labelPosition="center" />

          {mandateSaved ? (
            <Group justify="center" gap="xs">
              <Box c="green"><TbCheck size={16} /></Box>
              <Text size="sm" c="green">{t("mandateSaved")}</Text>
            </Group>
          ) : (
            <Group gap="sm">
              <TextInput
                flex={1}
                size="sm"
                placeholder={t("mandateNamePlaceholder")}
                value={mandateName}
                onChange={(e) => setMandateName(e.currentTarget.value)}
              />
              <Button
                size="sm"
                variant="outline"
                loading={isSaving}
                disabled={!mandateName.trim()}
                onClick={handleSaveMandate}
              >
                {t("saveAsMandate")}
              </Button>
            </Group>
          )}
          {mandateError && <Text size="xs" c="red">{mandateError}</Text>}
        </Stack>
      )}

      {/* Buy modal */}
      <ProcurementBuyModal
        result={buyTarget}
        criteria={buildCriteria()}
        onClose={() => setBuyTarget(null)}
      />
    </Stack>
  )
}
